import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Mail, Lock, Loader2, Wand2, CheckCircle2 } from "lucide-react";
import { GoogleSignInButton, OrDivider } from "@/components/GoogleSignInButton";
import { BrandLogo } from "@/components/BrandLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("password"); // password | magic
  const [magicSent, setMagicSent] = useState(false);

  const from = location.state?.from;
  const redirectTo = from ? `${from.pathname || "/dashboard"}${from.search || ""}` : "/dashboard";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "magic") {
        await axios.post(`${API}/auth/magic/request`, { email: email.trim().toLowerCase() });
        setMagicSent(true);
        return;
      }
      await login(email.trim().toLowerCase(), password);
      const hasPending = !!localStorage.getItem("adcprep_pending_diagnostic");
      navigate(hasPending ? "/diagnostic" : redirectTo, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Could not sign in. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <BrandLogo size={28} />
            <span className="font-bold text-lg">PassADC</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold mb-2">Welcome back</h1>
          <p className="text-muted-foreground text-sm">Sign in to continue your ADC prep.</p>
        </div>

        {mode === "password" && (
        <form
          onSubmit={handleSubmit}
          data-testid="login-form"
          className="glass rounded-2xl p-6 md:p-8 border border-border space-y-5"
        >
          {error && (
            <div data-testid="login-error" className="p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-sm text-destructive">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">Email</label>
            <div className="relative">
              <Mail className="w-4 h-4 text-muted-foreground absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                data-testid="login-email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-11 pr-4 py-3 rounded-xl bg-secondary border border-border text-sm focus:outline-none focus:border-primary/50"
                placeholder="you@example.com"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs uppercase tracking-wider text-muted-foreground">Password</label>
              <Link to="/forgot-password" className="text-xs text-primary hover:underline">Forgot?</Link>
            </div>
            <div className="relative">
              <Lock className="w-4 h-4 text-muted-foreground absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                data-testid="login-password"
                type="password"
                required={mode === "password"}
                minLength={6}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-11 pr-4 py-3 rounded-xl bg-secondary border border-border text-sm focus:outline-none focus:border-primary/50"
                placeholder="••••••••"
              />
            </div>
          </div>

          <Button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="w-full rounded-full py-6 text-base font-semibold"
          >
            {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing in...</>) : "Sign in"}
          </Button>

          <button
            type="button"
            data-testid="login-magic-toggle"
            onClick={() => { setMode("magic"); setError(""); }}
            className="w-full text-sm text-primary hover:underline inline-flex items-center justify-center gap-1.5"
          >
            <Wand2 className="w-3.5 h-3.5" /> Email me a sign-in link instead
          </button>

          <OrDivider />
          <GoogleSignInButton label="Sign in with Google" />
        </form>
        )}

        {mode === "magic" && !magicSent && (
        <form
          onSubmit={handleSubmit}
          data-testid="magic-form"
          className="glass rounded-2xl p-6 md:p-8 border border-border space-y-5"
        >
          {error && (
            <div data-testid="magic-error" className="p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-sm text-destructive">
              {error}
            </div>
          )}
          <div className="text-center">
            <div className="w-12 h-12 rounded-xl bg-primary/15 flex items-center justify-center mx-auto mb-3">
              <Wand2 className="w-6 h-6 text-primary" />
            </div>
            <p className="font-semibold">Sign in without a password</p>
            <p className="text-xs text-muted-foreground mt-1">We'll email you a one-tap link. Valid for 15 minutes.</p>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">Email</label>
            <div className="relative">
              <Mail className="w-4 h-4 text-muted-foreground absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                data-testid="magic-email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-11 pr-4 py-3 rounded-xl bg-secondary border border-border text-sm focus:outline-none focus:border-primary/50"
                placeholder="you@example.com"
              />
            </div>
          </div>
          <Button
            data-testid="magic-submit"
            type="submit"
            disabled={loading}
            className="w-full rounded-full py-6 text-base font-semibold"
          >
            {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending link...</>) : "Email me a sign-in link"}
          </Button>
          <button
            type="button"
            data-testid="magic-back-to-password"
            onClick={() => { setMode("password"); setError(""); }}
            className="w-full text-sm text-muted-foreground hover:text-foreground"
          >
            Use password instead
          </button>
        </form>
        )}

        {mode === "magic" && magicSent && (
          <div data-testid="magic-sent" className="glass rounded-2xl p-8 border border-border text-center space-y-3">
            <CheckCircle2 className="w-10 h-10 text-success mx-auto" />
            <p className="font-semibold">Check your inbox</p>
            <p className="text-sm text-muted-foreground">
              We sent a sign-in link to <strong>{email}</strong>. Tap it on this device to sign in - it's valid for 15 minutes.
            </p>
            <button
              type="button"
              data-testid="magic-resend"
              onClick={() => setMagicSent(false)}
              className="text-sm text-primary hover:underline"
            >
              Didn't get it? Send again
            </button>
          </div>
        )}

        <p className="text-center text-sm mt-6 text-muted-foreground">
          New here?{" "}
          <Link to="/signup" className="text-primary font-medium hover:underline" data-testid="login-to-signup">
            Create a free account
          </Link>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
